import { HttpStatusCode, TypeComponentNoti } from "@/enums/enums";
import { GetKeepLogin, SetCookiesAccessToken, SetCookiesRefreshToken } from "@/helper/userHelper";
import { i18n } from "@/i18n";
import API from "@/service/Index";
import store from "@/store/index";

const maxFileSize = 10;
const maxFileCountDefault = 10;
const fileTypeList = [
  ".pdf",
  ".png",
  ".jpg",
  ".gif",
  ".txt",
  ".mp4",
  ".mov",
  ".csv",
  ".xlsx",
  ".docx",
  ".zip",
  ".rar",
  ".7z",
  ".jpeg",
  ".pptx",
  ".key",
  ".rdx",
  ".heic",
  ".heif",
];

export function ReadFileAsync(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      resolve(e.target.result);
    };
    reader.readAsDataURL(file);
  });
}

async function ValidateTrueImage(file: File, nameFileType: string) {
  if (nameFileType == ".png" || nameFileType == ".jpg" || nameFileType == ".jpeg") {
    const rawString = await ReadFileAsync(file);
    const image: any = new Image();
    image.src = rawString;
    return await new Promise((resolve) => {
      image.onload = function() {
        if (image.height === 0 || image.width === 0) {
          resolve(false);
        }
        resolve(true);
      };
      image.onerror = () => {
        resolve(false);
      };
    });
  } else {
    return true;
  }
}

export async function ValidateUploadFile(
  file: File,
  fileType: string,
  fileList: any,
  fileCount: any,
  maxFileCount: number = maxFileCountDefault,
  allowedFileTypeList: Array<string> | null = null
) {
  const fileName = file.name;
  const fileTypeValidateList = allowedFileTypeList ? allowedFileTypeList : fileTypeList;

  if (fileName.length > 0) {
    let validateFileType = false;
    let nameFileType = "";
    let validateFileImage: any = true;
    for (let j = 0; j < fileTypeValidateList.length; j++) {
      const fileType = fileTypeValidateList[j];
      if (fileName.substr(fileName.length - fileType.length, fileType.length).toLowerCase() == fileType.toLowerCase()) {
        nameFileType = fileType.toLowerCase();
        validateFileType = true;
        break;
      }
    }

    if (!validateFileType) {
      store.dispatch(
        "Alert/SetAlertMessage",
        {
          alertMessage: i18n.t("Validate_Upload_File_Type", {
            filename: fileName,
            fileTypeList: fileTypeValidateList.join(", "),
          }),
          typeMessage: TypeComponentNoti.error,
        },
        { root: true }
      );
      return false;
    } else {
      validateFileImage = await ValidateTrueImage(file, nameFileType);
    }

    if (!validateFileImage) {
      store.dispatch(
        "Alert/SetAlertMessage",
        {
          alertMessage: i18n.t("Validate_Upload_File_True_Image"),
          typeMessage: TypeComponentNoti.error,
        },
        { root: true }
      );
      return false;
    }
  }

  if (file.size / 1024 / 1024 > maxFileSize) {
    store.dispatch(
      "Alert/SetAlertMessage",
      {
        alertMessage: i18n.t("Validate_Upload_File_larger", { maxFileSize: maxFileSize }),
        typeMessage: TypeComponentNoti.error,
      },
      { root: true }
    );
    return false;
  }

  if (fileCount > maxFileCount) {
    store.dispatch("Alert/SetAlertMessage", {
      alertMessage: i18n.t("Validate_Upload_File_Count", { maxFileCount: maxFileCount }),
      typeMessage: TypeComponentNoti.error,
    });
    return false;
  }

  if (fileList.some((fileItem: any) => fileItem.fileName === file.name)) {
    store.dispatch("Alert/SetAlertMessage", {
      alertMessage: i18n.t("Validate_Upload_File_duplicate"),
      typeMessage: TypeComponentNoti.error,
    });
    return false;
  }

  if (file.size === 0) {
    store.dispatch("Alert/SetAlertMessage", {
      alertMessage: i18n.t("Upload_Fail_No_Content"),
      typeMessage: TypeComponentNoti.error,
    });
    return false;
  }

  return true;
}

export function ValidFilesSizeUpload(fileSize: number, totalFilesSize: number) {
  if (totalFilesSize - fileSize < 0) {
    store.dispatch(
      "Alert/SetAlertMessage",
      {
        alertMessage: i18n.t("Validate_Upload_Total_Files_larger", { maxFileSize: maxFileSize }),
        typeMessage: TypeComponentNoti.error,
      },
      { root: true }
    );
    return false;
  }
  return true;
}

export function SetAlertAddFileStorage(
  responseList: Array<any>,
  isSuccess: boolean,
  idNoificition: number,
  input: any
) {
  if (isSuccess) {
    store.dispatch(
      "Alert/SetAlertMessage",
      {
        id: idNoificition,
        typeUpload: "End",
        alertMessage: i18n.t(
          "Upload_Success",
          input.fileList.length > 1 ? { msg: " " + responseList.length + " " } : { msg: input.fileList[0].fileName }
        ),
        typeMessage: TypeComponentNoti.Success,
      },
      { root: true }
    );
  } else {
    store.dispatch(
      "Alert/SetAlertMessage",
      {
        id: idNoificition,
        typeUpload: "End",

        alertMessage: i18n.t("Upload_Fail", { msg: " " + responseList.length + " " }),
        typeMessage: TypeComponentNoti.Error,
      },
      { root: true }
    );
  }
}

const IsAzureBlobStorageUrl = (url: string) => {
  try {
    return new URL(url).hostname.endsWith(".blob.core.windows.net");
  } catch (error) {
    return false;
  }
};

const GetFileServiceBaseUrl = () => {
  const base = process.env.VUE_APP_PeakFileService || "";
  return base.endsWith("/") ? base.slice(0, -1) : base;
};

export const ConvertFileUrlFromBlobStorageToFileService = (url: string) => {
  if (!url) return "";
  const fileServiceUrl = GetFileServiceBaseUrl();
  if (!fileServiceUrl || !IsAzureBlobStorageUrl(url)) {
    return url;
  }
  const { pathname, search } = new URL(url);
  return `${fileServiceUrl}${pathname}${search}`;
};

export const GetFileServiceUrl = (relativePath: string) => {
  if (!relativePath) return "";
  const base = GetFileServiceBaseUrl();
  if (!base) return "";
  const normalizedPath = relativePath.startsWith("/") ? relativePath.slice(1) : relativePath;
  return `${base}/${normalizedPath}`;
};

const MAX_PRIVATE_FILE_TOKEN_REFRESH = 2;
const PRIVATE_FILE_TIMEOUT = 30000;

const GetFileServiceRootUrl = () => GetFileServiceBaseUrl().replace(/\/file$/, "");

export const IsPrivateFileUrl = (url: string) => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return false;
  const fileServiceRoot = GetFileServiceRootUrl();
  if (!fileServiceRoot) return false;
  return IsAzureBlobStorageUrl(url) || url.startsWith(fileServiceRoot);
};

export const GetFileNameFromUrl = (url: string) => decodeURIComponent((url || "").split("?")[0].split("/").pop() || "");

const downloadOnlyFileTypeList = [".txt"];

const IsDownloadOnlyFileUrl = (url: string) => {
  const fileName = GetFileNameFromUrl(url).toLowerCase();
  return downloadOnlyFileTypeList.some((fileType) => fileName.endsWith(fileType));
};

const ShowCannotAccessFileAlert = () => {
  store.dispatch(
    "Alert/SetAlertMessage",
    {
      alertMessage: i18n.t("Cannot_Access_Image_From_Link"),
      typeMessage: TypeComponentNoti.Error,
    },
    { root: true }
  );
};

const RefreshAccessTokenForPrivateFile = async () => {
  const refreshResult = await API.AuthenService.DoGetNewAccessTokenByRefreshToken();
  if (refreshResult && refreshResult.status && refreshResult.status.IsSuccess()) {
    const keepLogin = GetKeepLogin();
    SetCookiesAccessToken(refreshResult.data.access_token);
    SetCookiesRefreshToken(refreshResult.data.refresh_token, keepLogin);
    return true;
  }
  return false;
};

const FetchWithTokenRefresh = async (url: string, options = {}) => {
  const fetchOptions = { ...options, credentials: "include" as const };
  let response = await fetch(url, fetchOptions);

  let refreshCount = 0;
  while (response.status === HttpStatusCode.Unauthorized && refreshCount < MAX_PRIVATE_FILE_TOKEN_REFRESH) {
    refreshCount += 1;
    let refreshed = false;
    try {
      refreshed = await RefreshAccessTokenForPrivateFile();
    } catch (error) {
      refreshed = false;
    }
    if (!refreshed) break;
    response = await fetch(url, fetchOptions);
  }

  if (response.status === HttpStatusCode.Unauthorized) {
    ShowCannotAccessFileAlert();
    throw new Error("Session expired - unable to load private file");
  }

  return response;
};

const fileBlobInFlight = new Map<string, Promise<Blob>>();

export const FetchFileBlob = (url: string) => {
  const isPrivate = IsPrivateFileUrl(url);
  const requestUrl = isPrivate ? ConvertFileUrlFromBlobStorageToFileService(url) : url;

  const pending = fileBlobInFlight.get(requestUrl);
  if (pending) return pending;

  const request = (isPrivate ? FetchWithTokenRefresh(requestUrl) : fetch(requestUrl))
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }
      return response.blob();
    })
    .finally(() => fileBlobInFlight.delete(requestUrl));

  fileBlobInFlight.set(requestUrl, request);
  return request;
};

export const FetchPrivateFileObjectUrl = async (url: string) => {
  if (!url || !IsPrivateFileUrl(url)) return url || "";
  const blob = await FetchFileBlob(url);
  return URL.createObjectURL(blob);
};

const SanitizeFileName = (fileName: string) => {
  if (!fileName) return "download";
  return (
    fileName
      .replace(/[<>:"/\\|?*]/g, "_")
      .replace(/\s+/g, "_")
      .substring(0, 255) || "download"
  );
};

const ResolveFileUrl = (url: string) => {
  if (!url) return "";
  if (/^(https?:|data:|blob:)/i.test(url)) return ConvertFileUrlFromBlobStorageToFileService(url);
  return GetFileServiceUrl(url);
};

export const DownLoadFromUrl = async (fileUrl: string, fileName: string) => {
  if (!fileUrl) {
    ShowCannotAccessFileAlert();
    return;
  }
  let objectUrl = "";
  const abortController = new AbortController();
  const timeoutId = window.setTimeout(() => abortController.abort(), PRIVATE_FILE_TIMEOUT);
  try {
    const requestUrl = ResolveFileUrl(fileUrl);
    const isPrivate = IsPrivateFileUrl(requestUrl);
    const requestOptions = { method: "GET", signal: abortController.signal };
    const response = isPrivate
      ? await FetchWithTokenRefresh(requestUrl, requestOptions)
      : await fetch(requestUrl, requestOptions);

    if (!response.ok) {
      ShowCannotAccessFileAlert();
      return;
    }

    objectUrl = window.URL.createObjectURL(await response.blob());
    const downloadLink = document.createElement("a");
    downloadLink.href = objectUrl;
    downloadLink.download = SanitizeFileName(fileName || GetFileNameFromUrl(fileUrl));
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  } catch (error) {
    ShowCannotAccessFileAlert();
  } finally {
    window.clearTimeout(timeoutId);
    if (objectUrl) window.URL.revokeObjectURL(objectUrl);
  }
};

export const OpenOrDownloadFileUrl = (url: string) => {
  if (!url) return;
  const fileUrl = ResolveFileUrl(url);
  const fileName = GetFileNameFromUrl(fileUrl);
  if (IsDownloadOnlyFileUrl(fileUrl)) {
    DownLoadFromUrl(fileUrl, fileName);
    return;
  }
  window.open(fileUrl);
};
